import moment from 'moment'
import type { EventActivity, OfflineQuotesQuery } from '@paintscout/api'
import type { CalendarEvent, OptionsDocument, QuoteArea, QuoteDocument } from 'paintscout'
import { filterItem, getAreaSubstrates, getDetails, getItems, getQuoteOptions } from '@paintscout/util/builder'
import { mapActivitiesToCalendarEvents } from '@paintscout/util/builder/util/map-activities-to-calendar-events'
import type {
  AdditionalWorkOption,
  AdditionalWorkOptionSubarea,
  PainterAdditionalWork,
  PainterContact,
  PainterScheduleEvent,
  PainterWorkItem,
  PainterWorkSubarea,
  WorkArea
} from '../PainterScheduleView'
import {
  getPainterAdditionalWorkCapabilities,
  getPainterAdditionalWorkOriginsFromQuote
} from './painterScheduleAdditionalWorkUtils'

type PainterSourceQuote = QuoteDocument & {
  deal_id?: string
  payments?: any[]
  additionalWork?: Record<string, any> | any[]
  internal_note?: string
  client_note?: string
  files?: any[]
  areas?: Record<string, any>
  lineItems?: Record<string, any>
  order?: {
    area?: Array<{ type?: string; key?: string }>
  }
  totals?: {
    hours?: number
    amountPaid?: number
    balanceDue?: number
  }
  totalHours?: number
}

interface BuildPainterScheduleEventsArgs {
  activities?: EventActivity[]
  dealFilesByDealId?: Record<string, any[]>
  dealJobAddressByDealId?: Record<string, any>
  offlineQuotes?: OfflineQuotesQuery['offlineQuotes']
  currentUserId?: string
  userCrewIds?: Set<string>
  options?: OptionsDocument
}

export function updatePainterEventSourceQuote(
  event: PainterScheduleEvent,
  sourceQuote: QuoteDocument,
  options?: OptionsDocument
): PainterScheduleEvent {
  const typedSourceQuote = sourceQuote as PainterSourceQuote

  return {
    ...event,
    dealId: event.dealId ?? typedSourceQuote.deal_id,
    jobAddress: event.jobAddress ?? stringifyAddress(typedSourceQuote.jobAddress),
    totalHours: getTotalHours(typedSourceQuote, { details: { deal: {} } } as EventActivity, options),
    crewNote: typedSourceQuote.internal_note || event.crewNote,
    workItems: buildWorkItems(typedSourceQuote),
    additionalWork: buildAdditionalWork(typedSourceQuote, options),
    sourceQuote
  }
}

export function buildPainterScheduleEvents(args: BuildPainterScheduleEventsArgs): PainterScheduleEvent[] {
  const {
    activities = [],
    dealFilesByDealId = {},
    dealJobAddressByDealId = {},
    offlineQuotes,
    currentUserId,
    userCrewIds,
    options
  } = args
  const assignedActivities = activities.filter((activity) => isAssignedToUser(activity, currentUserId, userCrewIds))
  const calendarEvents = mapActivitiesToCalendarEvents(assignedActivities, undefined, true)
  const quoteIndex = buildQuoteIndex(offlineQuotes)

  return calendarEvents.map((event, index) => {
    const activity = assignedActivities[index]
    const sourceQuote = getSourceQuote(activity, quoteIndex)
    const dealId =
      activity.details.deal?.id ??
      sourceQuote?.deal_id ??
      activity.details.invoice?.dealId ??
      activity.details.quote?.dealId

    return {
      id: event.id,
      dealId,
      dealName: activity.details.deal?.name ?? undefined,
      title: String(event.title ?? activity.details.deal?.name ?? 'Assigned Job'),
      startDate: new Date(event.start),
      endDate: new Date(event.end),
      jobAddress:
        stringifyAddress(dealJobAddressByDealId[dealId ?? '']) ||
        event.address ||
        stringifyAddress(sourceQuote?.jobAddress),

      contact: buildPainterContact(event),
      totalHours: getTotalHours(sourceQuote, activity, options),
      crewNote: sourceQuote?.internal_note,
      productString: (sourceQuote?.productString?.useCustom
        ? sourceQuote.productString.custom
        : sourceQuote?.productString?.default ?? '') as string,
      workItems: buildWorkItems(sourceQuote),
      additionalWork: buildAdditionalWork(sourceQuote, options),
      dealFiles: dealFilesByDealId[dealId ?? ''] ?? [],
      sourceQuote
    }
  })
}

export function buildPainterAdditionalWorkOptions(
  sourceQuote?: QuoteDocument,
  options?: OptionsDocument
): AdditionalWorkOption[] {
  if (!sourceQuote || !options) return []

  const quoteOptions = getQuoteOptions({ quote: sourceQuote, options })
  const showHours = quoteOptions?.showWorkOrderAreaValues ?? true
  const showPriceInstead = quoteOptions?.workOrderOptionsConsumer === 'customer'
  const useSubstrateView = (sourceQuote as PainterSourceQuote).defaultView === 'substrate'

  const substrateOptionEntries = getAreaSubstrateAdditionalWorkEntries(
    sourceQuote as PainterSourceQuote,
    options,
    'options'
  )
  const substrateOptionAreaKeys = new Set(substrateOptionEntries.map((item) => item.areaKey))
  const showCoats = (options as any).options?.quotes?.showWorkOrderCoats ?? false
  const showDescriptions = quoteOptions.showWorkOrderViewDescriptions ?? true

  const itemView = useSubstrateView ? 'substrate' : 'area'

  const itemOptions = getItems(
    showPriceInstead
      ? {
          quote: sourceQuote,
          section: 'options',
          view: itemView,
          consumer: 'customer',
          options,
          overrideOptions: { showPrices: true },
          hideDimensions: quoteOptions.hideWorkOrderDimensions
        }
      : {
          quote: sourceQuote,
          section: 'options',
          view: itemView,
          consumer: 'crew',
          options,
          overrideOptions: { showQuantities: true },
          showValues: showHours,
          hideDimensions: quoteOptions.hideWorkOrderDimensions
        }
  )
    .map((item) => {
      const subItems: any[] = item.subItems ?? []
      const subareas: AdditionalWorkOptionSubarea[] = subItems.map((sub) => {
        const parenIndex = (sub.name as string).indexOf(' (')
        const baseName = parenIndex === -1 ? sub.name : sub.name.substring(0, parenIndex)
        const subItemName = baseName + (parenIndex !== -1 ? sub.name.substring(parenIndex) : '')
        const coats = sub.additionalFields?.coats ? String(sub.additionalFields.coats) : undefined
        const hasContent = (html: string | undefined) => !!html?.replace(/<[^>]*>/g, '').trim()
        return {
          name: subItemName,
          hours: showPriceInstead
            ? undefined
            : sub.value != null && Number(sub.value) !== 0
              ? Number(sub.value)
              : undefined,
          description: hasContent(sub.description) ? sub.description : undefined,
          crewNote: hasContent(sub.crewNote) ? sub.crewNote : undefined,
          coats: showCoats && coats ? coats : undefined,
          reportText: showDescriptions && hasContent(sub.reportText) ? sub.reportText : undefined
        }
      })

      return {
        id: item.key,
        areaName: getRenderableItemName(item),
        optionName: getRenderableItemDescription(item) ?? getRenderableItemName(item),
        itemType: getAdditionalWorkItemType(item.type),
        hours: showPriceInstead ? undefined : showHours ? getRenderableItemHours(item) : undefined,
        price: showPriceInstead ? (typeof item.value === 'number' ? item.value : Number(item.value) || 0) : undefined,
        subareas: subareas.length > 0 ? subareas : undefined
      }
    })
    .filter((item) => !useSubstrateView || item.itemType !== 'area' || !substrateOptionAreaKeys.has(item.id))

  const substrateOptions = useSubstrateView
    ? substrateOptionEntries
        .filter((item) => !itemOptions.some((option) => option.id === item.key))
        .map((item) => ({
          id: item.key,
          areaId: item.areaKey,
          areaName: item.name,
          optionName: item.areaName,
          itemType: item.itemType,
          hours: showPriceInstead ? undefined : showHours ? item.hours : undefined,
          price: showPriceInstead ? item.price : undefined
        }))
    : []

  return [...itemOptions, ...substrateOptions]
}

export function getWorkAreasFromQuote(sourceQuote?: QuoteDocument): WorkArea[] {
  if (!sourceQuote) return []
  const typedQuote = sourceQuote as PainterSourceQuote

  // Try ordered areas first
  const orderedKeys = (typedQuote.order?.area ?? [])
    .filter((item) => item.type === 'area' && item.key)
    .map((item) => item.key as string)

  const keys = orderedKeys.length > 0 ? orderedKeys : Object.keys(typedQuote.areas ?? {})

  return keys
    .map((key) => {
      const area = typedQuote.areas?.[key]
      if (!area || area.show === false || (area.section && area.section !== 'bid')) return null
      return { id: key, name: getAreaName(area) }
    })
    .filter(Boolean) as WorkArea[]
}

export function getPainterAdditionalWorkItemKeys(
  sourceQuote?: QuoteDocument,
  options?: OptionsDocument,
  section: 'pending' | 'additional' = 'pending'
) {
  if (!sourceQuote || !options) return []

  const itemKeys = getItems({
    quote: sourceQuote,
    section,
    view: 'area',
    consumer: 'customer',
    options,
    overrideOptions: { showPrices: true }
  }).map((item) => item.key)
  const substrateKeys = getAreaSubstrateAdditionalWorkEntries(sourceQuote as PainterSourceQuote, options, section).map(
    (item) => item.key
  )
  const fallbackKeys = getFallbackAdditionalWorkEntries(sourceQuote as PainterSourceQuote, section).map(
    (item) => item.key
  )

  return Array.from(new Set([...itemKeys, ...substrateKeys, ...fallbackKeys]))
}

export function buildPainterScheduleActivitiesQuery(referenceDate = new Date()) {
  const start = moment(referenceDate).startOf('week').subtract(1, 'week').startOf('day').valueOf()
  const end = moment(referenceDate).startOf('week').add(2, 'weeks').endOf('week').endOf('day').valueOf()

  return [
    'details.activityType:event',
    'details.production:true',
    '-trashed:true',
    '-details.canceled:true',
    `details.end:>${start}`,
    `details.start:<${end}`
  ].join(' AND ')
}

function isAssignedToUser(activity: EventActivity, currentUserId?: string, userCrewIds?: Set<string>) {
  if (!currentUserId) return false

  if (userCrewIds && (activity.details as any)?.crew && userCrewIds.has((activity.details as any).crew)) {
    return true
  }

  const assignedUsers = activity.assignedUsers ?? {}
  const normalizedUserId = normalizeUserId(currentUserId)

  if (Array.isArray(assignedUsers)) {
    return assignedUsers.some((userId) => normalizeUserId(String(userId)) === normalizedUserId)
  }

  return Object.entries(assignedUsers).some(([userId, value]) => {
    return normalizeUserId(userId) === normalizedUserId && !!(value as { assigned?: boolean })?.assigned
  })
}

export function normalizeUserId(userId?: string | null) {
  return (userId ?? '').replace(/^auth0\|/, '')
}

function buildQuoteIndex(offlineQuotes?: OfflineQuotesQuery['offlineQuotes']) {
  const byId = new Map<string, PainterSourceQuote>()
  const byDealId = new Map<string, PainterSourceQuote>()

  for (const invoice of offlineQuotes?.invoices ?? []) {
    const typedInvoice = invoice as PainterSourceQuote
    byId.set(typedInvoice._id, typedInvoice)
    const dealId = getDealId(typedInvoice)
    if (dealId) {
      byDealId.set(dealId, typedInvoice)
    }
  }

  return { byId, byDealId }
}

function getSourceQuote(
  activity: EventActivity,
  quoteIndex: ReturnType<typeof buildQuoteIndex>
): PainterSourceQuote | undefined {
  const invoiceId = activity.details.invoice?.id
  const dealId = activity.details.deal?.id ?? activity.details.invoice?.dealId ?? activity.details.quote?.dealId

  if (invoiceId && quoteIndex.byId.has(invoiceId)) {
    return quoteIndex.byId.get(invoiceId)
  }

  if (dealId) {
    return quoteIndex.byDealId.get(dealId)
  }

  return undefined
}

function buildPainterContact(event: CalendarEvent): PainterContact | undefined {
  if (!event.contact) return undefined

  return {
    name: [event.contact.firstName, event.contact.lastName].filter(Boolean).join(' ').trim() || 'Customer',
    email: event.contact.email ?? undefined,
    phone: event.contact.phoneNumber ?? undefined
  }
}

function getTotalHours(
  sourceQuote: PainterSourceQuote | undefined,
  activity: EventActivity,
  options?: OptionsDocument
) {
  if (sourceQuote && options && sourceQuote.totals) {
    const details = getDetails({ quote: sourceQuote, options })
    const h = details.totals.hours
    const value = h.useCustom ? h.custom : h.default
    const num = typeof value === 'string' ? parseFloat(value) : value
    if (num != null && !isNaN(num)) return num
  }
  if (sourceQuote?.totals?.hours != null) return sourceQuote.totals.hours
  if (activity.details.deal?.hours != null) return activity.details.deal.hours
  const workItems = buildWorkItems(sourceQuote)
  if (workItems.length > 0) {
    return workItems.reduce((total, item) => total + (item.hours ?? 0), 0)
  }
}

function buildWorkItems(sourceQuote?: PainterSourceQuote): PainterWorkItem[] {
  if (!sourceQuote) return []

  const order = sourceQuote.order?.area ?? []
  const orderedItems = order
    .map((item) => {
      if (item.type === 'area' && item.key)
        return buildAreaWorkItem(item.key, sourceQuote.areas?.[item.key], sourceQuote)
      if (item.type === 'line_item' && item.key)
        return buildLineItemWorkItem(item.key, sourceQuote.lineItems?.[item.key])
      return null
    })
    .filter(Boolean) as PainterWorkItem[]

  if (orderedItems.length > 0) return orderedItems

  return [
    ...Object.entries(sourceQuote.areas ?? {}).map(([key, area]) => buildAreaWorkItem(key, area, sourceQuote)),
    ...Object.entries(sourceQuote.lineItems ?? {}).map(([key, lineItem]) => buildLineItemWorkItem(key, lineItem))
  ].filter(Boolean) as PainterWorkItem[]
}

function buildAreaWorkItem(id: string, area: QuoteArea, sourceQuote: PainterSourceQuote): PainterWorkItem | null {
  if (!area || area.show === false || !area.show_crew) return null
  const subareas = Object.values(area.substrates ?? {})
    .flat()
    .filter((substrate: any) => substrate?.show !== false)
    .map((substrate: any) => buildWorkSubarea(substrate))
    .filter(Boolean) as PainterWorkSubarea[]

  const hours: number = area?.totals?.override_hours
    ? area.totals.override_hours_value
    : area.totals?.hours ?? inferAreaHours(area, subareas) ?? 0

  const overrideQuoteHours = sourceQuote.totals?.override_hours
  return {
    id,
    name: getAreaName(area),
    hours: overrideQuoteHours ? undefined : hours,
    subareas
  }
}

function buildLineItemWorkItem(id: string, lineItem: any): PainterWorkItem | null {
  if (!lineItem || lineItem.show === false) return null

  return {
    id,
    name: lineItem.name ?? 'Line Item',
    hours: lineItem.hours ?? undefined,
    subareas: lineItem.description
      ? [
          {
            description: lineItem.description
          }
        ]
      : undefined
  }
}

function buildWorkSubarea(substrate: any): PainterWorkSubarea | null {
  if (!substrate) return null

  const productName =
    substrate.rate?.client_label ??
    substrate.rate?.label ??
    substrate.product?.product?.label ??
    substrate.selectedRate?.label ??
    undefined

  return {
    name:
      substrate.name ?? substrate.label ?? substrate.area_label?.custom ?? substrate.area_label?.default ?? undefined,
    productName,
    quantity: formatSubareaQuantity(substrate),
    coats: substrate.coats ?? undefined,
    hours: typeof substrate.hours === 'number' ? substrate.hours : undefined
  }
}

function formatSubareaQuantity(substrate: any) {
  if (substrate.quantity != null) return `${substrate.quantity}`
  if (substrate.length != null) return `${substrate.length}`
}

function inferAreaHours(area: any, subareas: PainterWorkSubarea[]) {
  const substrateHours: number = (Object.values(area.substrates ?? {}) as any[])
    .flat()
    .reduce((total: number, substrate: any) => total + (Number((substrate as any)?.hours) || 0), 0)

  if (substrateHours > 0) return substrateHours
  return subareas.length > 0 ? undefined : area.hours
}

function getAreaName(area: any) {
  return area?.area_label?.custom || area?.area_label?.default || area?.area_label?.value || area?.name || 'Area'
}

function buildAdditionalWork(sourceQuote?: PainterSourceQuote, options?: OptionsDocument): PainterAdditionalWork[] {
  if (!sourceQuote || !options) return []
  const origins = getPainterAdditionalWorkOriginsFromQuote(sourceQuote as QuoteDocument)

  const useSubstrateView = sourceQuote.defaultView === 'substrate'
  const view = useSubstrateView ? 'substrate' : 'area'

  const approvedDate = sourceQuote.additionalWork?.approvedDate
  const pendingItems = getItems({
    quote: sourceQuote,
    section: 'pending',
    view,
    consumer: 'customer',
    options,
    overrideOptions: { showPrices: true }
  })
  const approvedItems = getItems({
    quote: sourceQuote,
    section: 'additional',
    view,
    consumer: 'customer',
    options,
    overrideOptions: { showPrices: true }
  })

  const items = [
    ...pendingItems.map((item) => mapRenderableAdditionalWork(item, 'pending', approvedDate, origins, sourceQuote)),
    ...approvedItems.map((item) => mapRenderableAdditionalWork(item, 'approved', approvedDate, origins, sourceQuote))
  ]

  // In substrate view, track which areas are already represented by substrate items
  // so area-level fallback entries don't create duplicates
  const coveredAreaKeys = useSubstrateView
    ? new Set(
        [...pendingItems, ...approvedItems].flatMap((item: any) => (item?.additionalFields?.areaIds as string[]) ?? [])
      )
    : new Set<string>()

  const fallbackItems = [
    ...getFallbackAdditionalWorkEntries(sourceQuote, 'pending').map((item) =>
      mapFallbackAdditionalWork(item, 'pending', approvedDate, origins, sourceQuote)
    ),
    ...getFallbackAdditionalWorkEntries(sourceQuote, 'additional').map((item) =>
      mapFallbackAdditionalWork(item, 'approved', approvedDate, origins, sourceQuote)
    )
  ].filter(
    (item) =>
      !items.some((renderedItem) => renderedItem.sourceKey === item.sourceKey) && !coveredAreaKeys.has(item.sourceKey)
  )

  const combinedItems = [...items, ...fallbackItems]

  if (combinedItems.length > 0) return combinedItems

  const additionalWork = sourceQuote.additionalWork
  const entries = Array.isArray(additionalWork) ? additionalWork : Object.values(additionalWork ?? {})

  return entries
    .map((item: any, index: number) => {
      if (!item) return null

      const mappedItem = {
        id: item.id ?? item._id ?? `additional-work-${index}`,
        name: item.name ?? item.label ?? 'Additional Work',
        hours: item.hours ?? undefined,
        price: item.price ?? item.total ?? undefined,
        description: item.description ?? undefined,
        isCreated: !!item.isCreated,
        status: item.approvedDate ? 'approved' : 'pending',
        approvedDate: item.approvedDate ? formatTimestamp(item.approvedDate) : undefined,
        sourceKey: item.sourceKey ?? item.key ?? item.id ?? item._id ?? `additional-work-${index}`,
        sourceSection: item.approvedDate ? 'additional' : 'pending',
        itemType: item.itemType,
        hasMedia: !!(item.hasMedia || item.media?.length),
        media: item.media ?? undefined
      } as PainterAdditionalWork

      return {
        ...mappedItem,
        ...getPainterAdditionalWorkCapabilities(mappedItem, sourceQuote as QuoteDocument | undefined)
      } as PainterAdditionalWork
    })
    .filter(Boolean) as PainterAdditionalWork[]
}

function mapRenderableAdditionalWork(
  item: any,
  status: 'pending' | 'approved',
  approvedDate: number | string | undefined,
  origins: { preset: string[]; custom: string[] },
  sourceQuote?: PainterSourceQuote
) {
  const sourceKey = item.key
  const mappedItem = {
    id: sourceKey,
    name: getRenderableItemName(item),
    hours: getRenderableItemHours(item),
    price: typeof item.value === 'number' ? item.value : Number(item.value) || undefined,
    description: getRenderableItemDescription(item),
    isCreated: origins.custom.includes(sourceKey) ? true : false,
    status,
    approvedDate: status === 'approved' ? formatTimestamp(approvedDate) : undefined,
    sourceKey,
    sourceSection: status === 'approved' ? 'additional' : 'pending',
    itemType: getAdditionalWorkItemType(item.type)
  } as PainterAdditionalWork

  return {
    ...mappedItem,
    ...getPainterAdditionalWorkCapabilities(mappedItem, sourceQuote as QuoteDocument | undefined)
  } as PainterAdditionalWork
}

function mapFallbackAdditionalWork(
  item: any,
  status: 'pending' | 'approved',
  approvedDate: number | string | undefined,
  origins: { preset: string[]; custom: string[] },
  sourceQuote?: PainterSourceQuote
) {
  const mappedItem = {
    id: item.key,
    name: item.name ?? item.label ?? 'Additional Work',
    hours: item.hours ?? undefined,
    price: item.price ?? undefined,
    description: item.description ?? undefined,
    isCreated: origins.custom.includes(item.key) ? true : false,
    status,
    approvedDate: status === 'approved' ? formatTimestamp(approvedDate) : undefined,
    sourceKey: item.key,
    sourceSection: status === 'approved' ? 'additional' : 'pending',
    itemType: item.itemType
  } as PainterAdditionalWork

  return {
    ...mappedItem,
    ...getPainterAdditionalWorkCapabilities(mappedItem, sourceQuote as QuoteDocument | undefined)
  } as PainterAdditionalWork
}

function getAreaSubstrateAdditionalWorkEntries(
  sourceQuote: PainterSourceQuote,
  options: OptionsDocument,
  section: 'options' | 'pending' | 'additional'
) {
  return Object.entries(sourceQuote.areas ?? {}).flatMap(([areaKey, area]: any) => {
    if (!area?.substrates || !area?.substrateOrder) return []

    return getAreaSubstrates({
      quote: sourceQuote as QuoteDocument,
      options,
      areaKey,
      section,
      consumer: 'customer',
      overrideOptions: { showPrices: true },
      showGroups: true
    }).map((substrate: any) => ({
      key: substrate.key,
      areaKey,
      sourceSection: section,
      areaName: getAreaName(area),
      name: substrate.clientLabel || substrate.name || 'Surface',
      description: getAreaName(area),
      hours: getOverridableNumber(substrate.totalHours) ?? getOverridableNumber(substrate.hours),
      price: (() => {
        const p = getOverridableNumber(substrate.price) ?? 0
        const m = getOverridableNumber(substrate.materials) ?? 0
        const total = Math.round((p + m) * 100) / 100
        return total !== 0 ? total : undefined
      })(),
      itemType: 'substrate' as const
    }))
  })
}

function getFallbackAdditionalWorkEntries(sourceQuote: PainterSourceQuote, section: 'pending' | 'additional') {
  const lineItems = Object.values(sourceQuote.lineItems ?? {})
    .filter((item: any) => item?.section === section && item?.show !== false)
    .map((item: any) => ({
      key: item.key,
      name: item.name,
      description: item.description,
      hours: item.hours,
      price: item.price,
      itemType: 'lineItem' as const
    }))

  const areas = Object.entries(sourceQuote.areas ?? {})
    .filter(([, area]: any) => filterItem(area, section))
    .map(([key, area]: any) => ({
      key,
      name: getAreaName(area),
      description: undefined,
      hours: area.hours ?? area.totals?.hours,
      price: area.price ?? area.totals?.price,
      itemType: 'area' as const
    }))

  return [...lineItems, ...areas].filter((item) => !!item.key)
}

function getRenderableItemName(item: any) {
  const name = item?.name
  if (typeof name === 'string') return name
  return name?.custom || name?.default || 'Additional Work'
}

function getRenderableItemDescription(item: any) {
  const description = item?.description
  if (typeof description === 'string') return description || undefined
  return description?.custom || description?.default || undefined
}

function getRenderableItemHours(item: any) {
  return Number(item?.additionalFields?.painting ?? 0) + Number(item?.additionalFields?.prep ?? 0) || undefined
}

function getAdditionalWorkItemType(type?: string): PainterAdditionalWork['itemType'] {
  if (type === 'line_item') return 'lineItem'
  if (type === 'area' || type === 'substrate' || type === 'group') return type
  return undefined
}

function getOverridableNumber(value: any) {
  const rawValue = value?.useCustom ? value.custom : value?.custom ?? value?.default ?? value
  const numericValue = Number(rawValue)
  return Number.isFinite(numericValue) && numericValue !== 0 ? numericValue : undefined
}

function getDealId(sourceQuote?: PainterSourceQuote) {
  return sourceQuote?.deal_id ?? (sourceQuote as any)?.dealId ?? null
}

function stringifyAddress(address: any) {
  if (!address) return
  if (typeof address === 'string') return address

  return [address.address, address.city, address.province, address.postal].filter(Boolean).join(', ') || undefined
}

function formatTimestamp(value?: number | string) {
  if (!value) return
  const date = typeof value === 'number' ? moment(value) : moment(value)
  return date.isValid() ? date.format('MMM D, YYYY') : undefined
}
